import moment from "moment";
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select, { components } from "react-select";
import { Button, Card, CardBody, Col, Container, FormGroup, Input, Label, Row } from "reactstrap";
import Breadcrumbs from "../../../../layout/breadcrumb";
import BootstrapTable from "../../../../share-components/table/BootstrapTable";
import withReducer from "../../../../store/withReducer";
import partnerPromotionReducer from "../../store";
import DateRangePickerDefault from "../../../../share-components/ui/DateRangePickerDefault";
import { formatterCurrency } from "../../../../utils/common";
import {
  getPartnerPromotionSettings,
  setPartnerPromotionSettings,
  getPromotionItems,
  exportPromotionItems,
} from "../../store/partnerPromotionSlice";

const isValidDate = (d) => d !== null && d !== undefined && moment(d).isValid();

const tableColumns = [
  {
    name: "Partner Name",
    selector: (row) => row.partner_name,
    sortable: true,
    left: true,
  },
  {
    name: "Start Date",
    selector: (row) => row.start_date,
    sortable: true,
    center: true,
    width: "110px",
  },
  {
    name: "End Date",
    selector: (row) => row.end_date,
    sortable: true,
    center: true,
    width: "110px",
  },
  {
    name: "Promotion Name",
    selector: (row) => row.promotion_name,
    sortable: true,
    left: true,
  },
  {
    name: "SKU",
    selector: (row) => row.sku,
    sortable: true,
    left: true,
    width: "140px",
  },
  {
    name: "RRP",
    selector: (row) => formatterCurrency(row.rrp || 0),
    sortable: true,
    right: true,
    width: "100px",
  },
  {
    name: "Sale Price",
    selector: (row) => formatterCurrency(row.sale_price || 0),
    sortable: true,
    right: true,
    width: "110px",
  },
  {
    name: "Promotion %",
    selector: (row) => row.promotion_percentage ? `${parseFloat(row.promotion_percentage).toFixed(2)}%` : "0.00%",
    sortable: true,
    right: true,
    width: "120px",
  },
  {
    name: "Promotion RRP",
    selector: (row) => formatterCurrency(row.promotion_price || 0),
    sortable: true,
    right: true,
    width: "130px",
  },
];

const dataMenu = [
  { type: 1, text: "Partners Promotions", link: "/product/partners-promotions" },
  { type: 0, text: "Details" },
];

const today = new Date();
const oneMonthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());

function PartnerPromotionDetails() {
  const dispatch = useDispatch();

  const allSettings = useSelector(({ partnerPromotion }) => partnerPromotion.partnerPromotion?.settings);
  const promotionItemsState = useSelector(({ partnerPromotion }) => partnerPromotion.partnerPromotion?.promotionItems);

  const [loading, setLoading] = useState(false);
  const [isDownloadReport, setIsDownloadReport] = useState(false);
  const [timer, setTimer] = useState(null);

  const [promotionDateRange, setPromotionDateRange] = useState([oneMonthAgo, today]);
  const [promotionStartDate, promotionEndDate] = promotionDateRange;

  const [filterPartner, setFilterPartner] = useState(null);
  const [filterPromotionName, setFilterPromotionName] = useState("");
  const [filterSKU, setFilterSKU] = useState("");

  const [lastFilters, setLastFilters] = useState(null);
  const [filters, setFilters] = useState({
    page: 1,
    pageSize: 25,
    start_date: moment(oneMonthAgo).format("YYYY-MM-DD"),
    end_date: moment(today).format("YYYY-MM-DD"),
    partner_id: null,
    promotion_name: null,
    sku: null,
  });

  useEffect(() => {
    dispatch(getPartnerPromotionSettings()).then((res) => {
      dispatch(setPartnerPromotionSettings(res.payload));
    });
  }, [dispatch]);

  useEffect(() => {
    if (JSON.stringify(filters) === JSON.stringify(lastFilters)) return;
    setLastFilters(filters);
    setLoading(true);
    dispatch(getPromotionItems(filters)).then(() => {
      setLoading(false);
    });
  }, [dispatch, filters]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      start_date: isValidDate(promotionStartDate) ? moment(promotionStartDate).format("YYYY-MM-DD") : null,
      end_date: isValidDate(promotionEndDate) ? moment(promotionEndDate).format("YYYY-MM-DD") : null,
      page: 1,
    }));
  }, [promotionStartDate, promotionEndDate]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleTextInputChange = (key, value) => {
    clearTimeout(timer);
    const newTimer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, [key]: value || null, page: 1 }));
    }, 500);
    setTimer(newTimer);
  };

  const clearFilters = () => {
    setPromotionDateRange([oneMonthAgo, today]);
    setFilterPartner(null);
    setFilterPromotionName("");
    setFilterSKU("");
    setFilters({
      page: 1,
      pageSize: 25,
      start_date: moment(oneMonthAgo).format("YYYY-MM-DD"),
      end_date: moment(today).format("YYYY-MM-DD"),
      partner_id: null,
      promotion_name: null,
      sku: null,
    });
  };

  const eventExport = () => {
    if (isDownloadReport) return;
    setIsDownloadReport(true);
    dispatch(exportPromotionItems(filters)).then((res) => {
      const link = document.createElement("a");
      link.href = res.payload;
      link.setAttribute("download", "");
      document.body.appendChild(link);
      link.click();
      link.remove();
      setIsDownloadReport(false);
    });
  };

  const onPageChange = (e) => setFilters((prev) => ({ ...prev, page: e }));
  const onPageSizeChange = (newPageSize) => {
    setFilters((prev) => ({
      ...prev,
      page: 1,
      pageSize: newPageSize === "All" ? promotionItemsState?.totalRow ?? 25 : newPageSize,
    }));
  };

  return (
    <Fragment>
      <Breadcrumbs title="Promotion Details" data={dataMenu} />
      <Container fluid={true}>
        <Card className="px-3 mb-3">
          <CardBody>
            <Row>
              <Col md="12">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Filters</h6>
                  <div>
                    <button
                      type="button"
                      onClick={eventExport}
                      className="btn btn-sm btn-dark mr-2"
                      disabled={isDownloadReport}
                    >
                      <i className={isDownloadReport ? "fa fa-spinner fa-spin mr-1" : "fa fa-download mr-1"}></i>
                      Export
                    </button>
                    <Button color="secondary" size="sm" onClick={clearFilters}>
                      Clear Filters
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>

            <Row>
              <Col md="">
                <FormGroup>
                  <Label>Promotion Date</Label>
                  <DateRangePickerDefault
                    placeholder="Search promotion date range"
                    dateRange={promotionDateRange}
                    dateRangeChange={(date) => {
                      setPromotionDateRange(date === null ? [null, null] : date);
                    }}
                  />
                </FormGroup>
              </Col>
              <Col md="">
                <FormGroup>
                  <Label>Partner</Label>
                  <Select
                    className="select2-filter"
                    placeholder="Search by partner"
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.id}
                    value={filterPartner}
                    onChange={(e) => {
                      handleFilterChange("partner_id", e ? e.id : null);
                      setFilterPartner(e);
                    }}
                    components={{
                      IndicatorSeparator: () => null,
                      DropdownIndicator: (props) =>
                        filterPartner ? null : <components.DropdownIndicator {...props} />,
                    }}
                    isClearable
                    options={allSettings?.partners || []}
                  />
                </FormGroup>
              </Col>
              <Col md="">
                <FormGroup>
                  <Label>Promotion Name</Label>
                  <Input
                    type="text"
                    placeholder="Search by promotion name"
                    value={filterPromotionName}
                    onChange={(e) => {
                      handleTextInputChange("promotion_name", e.target.value);
                      setFilterPromotionName(e.target.value);
                    }}
                  />
                </FormGroup>
              </Col>
              <Col md="">
                <FormGroup>
                  <Label>SKU</Label>
                  <Input
                    type="text"
                    placeholder="Search by SKU"
                    value={filterSKU}
                    onChange={(e) => {
                      handleTextInputChange("sku", e.target.value);
                      setFilterSKU(e.target.value);
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className="px-3">
          <CardBody style={{ fontSize: "12px" }}>
            <BootstrapTable
              headerColumns={tableColumns}
              gridData={promotionItemsState?.data || []}
              noAction
              progressPending={loading}
              paginationServer
              paginationTotalRows={promotionItemsState?.totalRow || 0}
              currentPage={filters.page}
              perPage={filters.pageSize}
              onChangePage={onPageChange}
              onChangeRowsPerPage={onPageSizeChange}
              size="md"
            />
          </CardBody>
        </Card>
      </Container>
    </Fragment>
  );
}

export default withReducer([{ partnerPromotion: partnerPromotionReducer }])(PartnerPromotionDetails);
